import { useEffect, useRef, useState, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { environment } from '../config/firebase.config';
import { loginMachine } from '../auth/loginMachine';

// Google OAuth 2.0 Web Client IDs
const GOOGLE_CLIENT_IDS = {
  development: '920078455554-sc470hfo4v92jhbk3f29mm7t8uq3rr45.apps.googleusercontent.com',
  production: '371859504455-aj8p343ubd518pp4l6vsta37894qitic.apps.googleusercontent.com'
};

const GOOGLE_CLIENT_ID = GOOGLE_CLIENT_IDS[environment];
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GSI_SCRIPT_ID = 'google-gsi-client';
let gsiScriptPromise = null;

// Context for managing One Tap state globally
const OneTapContext = createContext();

function loadGsiScript() {
  if (window.google?.accounts?.id) {
    return Promise.resolve(true);
  }

  if (gsiScriptPromise) {
    return gsiScriptPromise;
  }

  gsiScriptPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      resolve(loaded);
    };

    const existingScript = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`) ||
      document.getElementById(GSI_SCRIPT_ID);

    const script = existingScript || document.createElement('script');
    script.id = script.id || GSI_SCRIPT_ID;
    script.src = script.src || GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => finish(Boolean(window.google?.accounts?.id));
    script.onerror = () => {
      console.warn('[OneTapManager] GSI script request failed');
      finish(false);
    };

    if (!existingScript) {
      document.head.appendChild(script);
    }

    const checkGsi = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        window.clearInterval(checkGsi);
        finish(true);
      }
    }, 100);

    window.setTimeout(() => {
      window.clearInterval(checkGsi);
      finish(Boolean(window.google?.accounts?.id));
    }, 10000);
  });

  return gsiScriptPromise;
}

// Singleton state manager - lives outside React to survive rerenders
class OneTapState {
  constructor() {
    this.initialized = false;
    this.showing = false;
    this.cooldown = false;
    this.callbacks = {};
    this.lastDismissTime = 0;
    this.showPromise = null;
  }

  setCallback(type, fn) {
    this.callbacks[type] = fn;
  }

  isShowing() {
    return this.showing;
  }

  async initialize() {
    if (this.initialized) return false;

    // Check if in cooldown (2 minutes after dismiss)
    if (this.cooldown && Date.now() - this.lastDismissTime < 120000) {
      console.log('[OneTapManager] Still in cooldown period');
      return false;
    }

    if (!window.google?.accounts?.id) {
      console.log('[OneTapManager] GSI library not loaded');
      return false;
    }

    if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID.includes('YOUR_')) {
      console.warn('[OneTapManager] Google Client ID not configured');
      return false;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => this.handleCredential(response),
        auto_select: true,
        cancel_on_tap_outside: false,
        context: 'signin',
        itp_support: true,
        use_fedcm_for_prompt: true
      });

      this.initialized = true;
      return true;
    } catch (error) {
      console.error('[OneTapManager] Initialization error:', error);
      return false;
    }
  }

  async show() {
    // Simple show - no complex logic
    if (this.showing) return true;

    if (!this.initialized) {
      await this.initialize();
    }

    if (!window.google?.accounts?.id) {
      return false;
    }

    try {
      // Start with showing = false
      this.showing = false;

      window.google.accounts.id.prompt((notification) => {
        // With FedCM enabled, display moment notifications don't work
        // We can only reliably detect dismissed and skipped moments

        if (notification.isDismissedMoment()) {
          // User closed it or credential was retrieved
          console.log('[OneTapManager] User dismissed One Tap');
          this.showing = false;
          if (this.callbacks.onDismissed) {
            this.callbacks.onDismissed();
          }
        } else if (notification.isSkippedMoment()) {
          // Skipped for some reason (cooldown, etc)
          console.log('[OneTapManager] Skipped:', notification.getSkippedReason?.() || 'unknown');
          this.showing = false;
          if (this.callbacks.onSkipped) {
            this.callbacks.onSkipped();
          }
        }
      });

      return true;
    } catch (error) {
      console.error('[OneTapManager] Error showing prompt:', error);
      this.showing = false;
      return false;
    }
  }


  hide() {
    if (this.initialized && window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
      this.showing = false;
    }
  }

  async handleCredential(response) {
    console.log('[OneTapManager] Credential received');

    try {
      const idToken = response.credential;
      const result = await loginMachine.continueWithGoogleIdToken(idToken, this.callbacks.reason || 'manual');

      console.log('[OneTapManager] Sign-in successful:', result?.email);

      if (this.callbacks.onSuccess) {
        this.callbacks.onSuccess(result);
      }
    } catch (error) {
      console.error('[OneTapManager] Sign-in error:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    }
  }

  reset() {
    this.hide();
    this.initialized = false;
    this.cooldown = false;
    this.lastDismissTime = 0;
  }
}

// Create singleton instance
const oneTapState = new OneTapState();

// Provider component - should be placed at the root of your app
export function GoogleOneTapProvider({ children }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadGsiScript().then((loaded) => {
      if (cancelled) return;
      if (loaded) {
        setIsReady(true);
      } else {
        console.warn('[OneTapManager] GSI library failed to load');
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <OneTapContext.Provider value={{ oneTapState, isReady }}>
      {children}
    </OneTapContext.Provider>
  );
}

// Hook to use One Tap functionality
export function useGoogleOneTap() {
  const context = useContext(OneTapContext);
  if (!context) {
    throw new Error('useGoogleOneTap must be used within GoogleOneTapProvider');
  }
  return context;
}

// Portal-based component that renders outside the main React tree
// This prevents it from being affected by parent rerenders
export function GoogleOneTapPortal({
  show = false,
  onSuccess,
  onError,
  onNotDisplayed
}) {
  const { oneTapState, isReady } = useGoogleOneTap();
  const portalRef = useRef(null);
  const activeRef = useRef(false);

  // Create portal container
  useEffect(() => {
    if (!portalRef.current) {
      portalRef.current = document.createElement('div');
      portalRef.current.id = 'google-one-tap-portal';
      document.body.appendChild(portalRef.current);
    }

    return () => {
      if (portalRef.current && document.body.contains(portalRef.current)) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  // Set callbacks
  useEffect(() => {
    oneTapState.setCallback('onSuccess', onSuccess);
    oneTapState.setCallback('onError', onError);
    oneTapState.setCallback('onNotDisplayed', onNotDisplayed);
  }, [oneTapState, onSuccess, onError, onNotDisplayed]);

  // Handle show/hide
  useEffect(() => {
    if (!isReady) return;

    const handleShow = async () => {
      if (show && !activeRef.current) {
        activeRef.current = true;
        await oneTapState.show();
      } else if (!show && activeRef.current) {
        activeRef.current = false;
        oneTapState.hide();
      }
    };

    handleShow();

    return () => {
      // Don't hide on cleanup - let it persist
      activeRef.current = false;
    };
  }, [show, isReady, oneTapState]);

  // Portal renders nothing visible - One Tap creates its own UI
  return portalRef.current ? ReactDOM.createPortal(null, portalRef.current) : null;
}

// Standalone trigger component - use this instead of GoogleOneTap
export function GoogleOneTapTrigger({
  autoShow = false,
  delay = 1000,
  onSuccess,
  onError,
  onNotDisplayed,
  onDisplayed,
  onDismissed,
  reason,
  disabled = false
}) {
  const { oneTapState, isReady } = useGoogleOneTap();
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (disabled || !isReady || !autoShow || hasShownRef.current) return;

    const timer = setTimeout(async () => {
      if (!hasShownRef.current) {
        hasShownRef.current = true;
        oneTapState.setCallback('onSuccess', onSuccess);
        oneTapState.setCallback('onError', onError);
        oneTapState.setCallback('onNotDisplayed', onNotDisplayed);
        oneTapState.setCallback('onSkipped', onNotDisplayed);
        oneTapState.setCallback('onDismissed', onDismissed || onNotDisplayed);
        oneTapState.setCallback('onDisplayed', onDisplayed);
        oneTapState.setCallback('reason', reason);

        const shown = await oneTapState.show();
        if (!shown && onNotDisplayed) {
          onNotDisplayed('not-shown');
        }
        // If One Tap was shown successfully, call onDisplayed
        if (shown && onDisplayed) {
          onDisplayed();
        }
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [autoShow, delay, disabled, isReady, oneTapState, onSuccess, onError, onNotDisplayed, onDisplayed, onDismissed, reason]);

  return null;
}
